import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import HitCard from 'Components/Cards/HitCard';
import CarouselArrow from 'Components/Carousel/CarouselArrow';
import CarouselPaging from 'Components/Carousel/CarouselPaging';
import ViewAllCard from 'Components/Cards/ViewAllCard';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import useViewport from 'Hooks/useViewport';
import imageSizes from 'Components/Cards/image-sizes';
import SiteSlider from 'Components/Utility/SiteSlider/SiteSlider';

function ProductCarousel({
  hitData,
  hitCardLightTheme,
  slidesToShow,
  mdSlidesToShow,
  smSlidesToShow,
  place,
  resetCarousel,
  viewAllLocation,
  viewAllTitle,
  leftAlign,
  showFlashDealBar,
  showFakeCards,
  lazyLoadCover,
  hitCardNameTag,
  carouselCoverSize,
  newCarousel,
}) {
  const sliderRef = useRef();

  const { isBelowLg } = useViewport();

  if (!hitData) return false;

  const resetSlickCarousel = () => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  };

  if (resetCarousel) {
    resetSlickCarousel();
  }

  const settings = {
    infinite: false,
    prevArrow: <CarouselArrow ariaLabel="Previous" arrowDirection="left" containerClassName="arrow-btn-container" />,
    nextArrow: <CarouselArrow ariaLabel="Next" arrowDirection="right" containerClassName="arrow-btn-container" />,
    slidesToShow: slidesToShow || 3,
    slidesToScroll: slidesToShow || 3,
    centerMode: slidesToShow === 1,
    centerPadding: slidesToShow === 1 ? '20px' : '0px',
    dots: true,
    lazyLoad: true,
    initialSlide: 0,
    accessibility: true,
    customPaging: i => (
      <CarouselPaging
        slideIndex={i}
        slideLength={Array.isArray(hitData) ? hitData.length : 0}
        slidesToShow={slidesToShow || 3}
      />
    ),
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: slidesToShow || 3,
          slidesToScroll: slidesToShow || 3,
        },
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: mdSlidesToShow || 2,
          slidesToScroll: mdSlidesToShow || 2,
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(hitData) ? hitData.length : 0}
              slidesToShow={mdSlidesToShow || 2}
            />
          ),
        },
      },
      {
        breakpoint: 767,
        settings: {
          arrows: false,
          centerMode: smSlidesToShow === 1,
          swipeToSlide: smSlidesToShow === 1,
          centerPadding: smSlidesToShow === 1 ? '30px' : false,
          slidesToShow: smSlidesToShow || 2,
          slidesToScroll: smSlidesToShow || 2,
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(hitData) ? hitData.length : 0}
              slidesToShow={smSlidesToShow || 2}
            />
          ),
        },
      },
      {
        breakpoint: 480,
        settings: {
          arrows: false,
          centerMode: true,
          centerPadding: '30px',
          slidesToScroll: 1,
          slidesToShow: 1,
          swipeToSlide: true,
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(hitData) ? hitData.length : 0}
              slidesToShow={1}
            />
          ),
        },
      },
    ],
  };

  if (newCarousel && isEmpty(hitData) && showFakeCards) {
    return (
      <SiteSlider sliderClass="ProductCardCarousel">
        {[...Array(12).keys()].map(fakeHit =>
          <SkeletonHitCard
            key={fakeHit + 1}
            numberOfCards={1}
            xsSize={12}
            smSize={12}
            mdSize={12}
            lgSize={12}
            showFlashDealBar={showFlashDealBar}
          />,
        )}
      </SiteSlider>
    );
  }

  if (!newCarousel && isEmpty(hitData) && showFakeCards) {
    return (
      <Slider ref={sliderRef} {...settings} className={classNames('product-slider', { 'slider-light-theme': hitCardLightTheme })}>
        {isEmpty(hitData) && [...Array(12).keys()].map(fakeHit =>
          <SkeletonHitCard
            key={fakeHit + 1}
            numberOfCards={1}
            xsSize={12}
            smSize={12}
            mdSize={12}
            lgSize={12}
            showFlashDealBar={showFlashDealBar}
          />,
        )}
      </Slider>
    );
  }

  // Calculate image sizes separately
  let sizesOverride = imageSizes(4, 6, 6, 12);
  if (slidesToShow === 2) {
    sizesOverride = imageSizes(6, 6, 6, 12);
  }
  if (slidesToShow === 4) {
    sizesOverride = imageSizes(3, 6, 6, 12);
  }
  let hitDataLength = hitData.length;
  if (viewAllLocation && viewAllLocation !== 'wishlist' && hitDataLength >= (slidesToShow * 3)) (hitDataLength -= 1);
  if (viewAllLocation && viewAllLocation === 'wishlist' && hitDataLength === 15) (hitDataLength -= 1);

  let useCarousel = true;
  if (hitData.length <= slidesToShow) useCarousel = false;
  if (isBelowLg) useCarousel = true;

  if (!useCarousel) {
    return (
      <div className={classNames('fan-grid-container carousel-fallback', { 'left-align': leftAlign })}>
        {hitData.slice(0, hitDataLength).map(hit =>
          <div
            className={classNames({ 'two-column-row': slidesToShow === 2 }, { 'three-column-row': slidesToShow === 3 }, { 'four-column-row': slidesToShow === 4 })}
            key={hit.product_id + Math.random()}
          >
            <HitCard
              showFlashDealBar={showFlashDealBar}
              hitCardLightTheme={hitCardLightTheme}
              xsSize={12}
              smSize={12}
              mdSize={12}
              lgSize={12}
              sizesOverride={sizesOverride}
              hit={hit}
              place={place}
              lazy={lazyLoadCover}
              hitCardNameTag={hitCardNameTag}
              coverTypeOverride={carouselCoverSize}
              adaptiveSize={carouselCoverSize === 'portrait' || carouselCoverSize === 'square'}
            />
          </div>,
        )}
      </div>
    );
  }

  let showViewAllCard = false;
  if (viewAllLocation && viewAllLocation !== '') showViewAllCard = true;

  if (newCarousel) {
    sizesOverride = '(min-width: 1520px) 307px, (min-width: 1200px) 263px, (min-width: 992px) 298px, (min-width: 768px) 309px, (min-width: 576px) 228px, 95vw';
    return (
      <SiteSlider sliderClass="ProductCardCarousel">
        {hitData.slice(0, hitDataLength).map(hit =>
          <HitCard
            key={hit.product_id + Math.random()}
            showFlashDealBar={showFlashDealBar}
            hitCardLightTheme={hitCardLightTheme}
            xsSize={12}
            smSize={12}
            mdSize={12}
            lgSize={12}
            sizesOverride={sizesOverride}
            hit={hit}
            place={place}
            lazy={lazyLoadCover}
            hitCardNameTag={hitCardNameTag}
            coverTypeOverride={carouselCoverSize}
            adaptiveSize={carouselCoverSize === 'portrait' || carouselCoverSize === 'square'}
          />,
        )}
        {showViewAllCard &&
          <ViewAllCard
            key={Math.random()}
            title={viewAllTitle}
            location={viewAllLocation}
            cardAspectSize={carouselCoverSize}
            showFlashDealBar={showFlashDealBar}
          />
        }
      </SiteSlider>
    );
  }

  return (
    <Slider ref={sliderRef} {...settings} className={classNames('product-slider', { 'slider-light-theme': hitCardLightTheme })}>
      {hitData.slice(0, hitDataLength).map(hit =>
        <HitCard
          showFlashDealBar={showFlashDealBar}
          hitCardLightTheme={hitCardLightTheme}
          xsSize={12}
          smSize={12}
          mdSize={12}
          lgSize={12}
          sizesOverride={sizesOverride}
          key={hit.product_id + Math.random()}
          hit={hit}
          place={place}
          lazy={lazyLoadCover}
          hitCardNameTag={hitCardNameTag}
          coverTypeOverride={carouselCoverSize}
          adaptiveSize={carouselCoverSize === 'portrait' || carouselCoverSize === 'square'}
        />,
      )}
      {viewAllLocation &&
        <ViewAllCard
          title={viewAllTitle}
          location={viewAllLocation}
          cardAspectSize={carouselCoverSize}
          showFlashDealBar={showFlashDealBar}
        />
      }
    </Slider>
  );
}

ProductCarousel.propTypes = {
  hitData: PropTypes.arrayOf(PropTypes.shape),
  hitCardLightTheme: PropTypes.bool,
  slidesToShow: PropTypes.number,
  mdSlidesToShow: PropTypes.number,
  smSlidesToShow: PropTypes.number,
  place: PropTypes.string, // where the ProductCarousel is placed on within the website
  resetCarousel: PropTypes.bool, // set carousel back to the first slide
  viewAllLocation: PropTypes.string, // path which the last item in the carousel should link to
  viewAllTitle: PropTypes.string,
  leftAlign: PropTypes.bool,
  showFlashDealBar: PropTypes.bool,
  showFakeCards: PropTypes.bool,
  lazyLoadCover: PropTypes.bool,
  hitCardNameTag: PropTypes.string,
  carouselCoverSize: PropTypes.oneOf([
    '',
    '16x9',
    'portrait',
    'square',
  ]),
  newCarousel: PropTypes.bool,
};

ProductCarousel.defaultProps = {
  hitData: [],
  hitCardLightTheme: false,
  place: '',
  slidesToShow: 3,
  mdSlidesToShow: 2,
  smSlidesToShow: 2,
  resetCarousel: false,
  viewAllLocation: '',
  viewAllTitle: '',
  leftAlign: false,
  showFlashDealBar: false,
  showFakeCards: false,
  lazyLoadCover: true,
  hitCardNameTag: 'div',
  carouselCoverSize: '',
  newCarousel: false,
};

export default ProductCarousel;
